import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// Host-only full-screen "roll-call" view.
// Renders one card per participant with their personal seat-link QR so the host
// can pass a single phone/laptop around the room and every friend scans their
// own rejoin link onto their own device.
//
// Props:
//   open         - boolean
//   onClose      - () => void
//   title        - string shown in the top bar (e.g. "QR Test · Roll-call")
//   subtitle     - optional helper text
//   participants - array of { id, username, is_host?, is_spectator? }
//   linkBase     - e.g. `${origin}/draft/ABCD` or `${origin}/contest/ABCD`
//                  Each card's QR encodes `${linkBase}?as=${participant.id}`.
export default function RollCallView({
  open,
  onClose,
  title,
  subtitle,
  participants,
  linkBase,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-pbo-green-deep text-pbo-cream overflow-y-auto"
      data-testid="roll-call-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Roll-call — seat link QRs"
    >
      <div className="sticky top-0 z-10 bg-pbo-green-deep/95 backdrop-blur border-b border-pbo-gold/30 px-6 py-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-pbo-gold-soft">
            Roll-call
          </div>
          <div className="font-serif text-2xl text-pbo-cream truncate">
            {title}
          </div>
          {subtitle && (
            <div className="text-xs text-pbo-cream/70 mt-0.5">{subtitle}</div>
          )}
        </div>
        <Button
          variant="outline"
          onClick={onClose}
          className="bg-transparent border-pbo-gold/40 text-pbo-cream hover:bg-pbo-green hover:text-pbo-gold-soft shrink-0"
          data-testid="roll-call-close-btn"
        >
          <X className="w-4 h-4 mr-1.5" />
          Close
        </Button>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        <p className="text-sm text-pbo-cream/80 mb-6 max-w-2xl">
          Pass this around. Each friend points their phone camera at their own
          card — scanning it opens their personal rejoin link on their device so
          they&apos;re in as themselves.
        </p>

        {participants.length === 0 ? (
          <div className="text-center text-pbo-cream/60 italic py-20">
            No one&apos;s in the room yet.
          </div>
        ) : (
          <div
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="roll-call-grid"
          >
            {participants.map((p) => {
              const url = `${linkBase}?as=${p.id}`;
              return (
                <div
                  key={p.id}
                  className="bg-pbo-cream text-pbo-ink rounded-2xl p-5 flex flex-col items-center text-center shadow-lg ring-1 ring-pbo-gold/30"
                  data-testid={`roll-call-card-${p.id}`}
                >
                  <div className="font-serif text-pbo-green text-xl leading-tight mb-1 truncate w-full">
                    {p.username}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-3 h-4">
                    {p.is_host && (
                      <span className="text-pbo-gold font-semibold">Host</span>
                    )}
                    {p.is_spectator && (
                      <span className="text-pbo-muted">Watching</span>
                    )}
                  </div>
                  <div className="bg-white p-3 rounded-xl ring-1 ring-pbo-gold/30">
                    <QRCodeSVG
                      value={url}
                      size={180}
                      level="M"
                      fgColor="#0F4D2A"
                      bgColor="#FFFFFF"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
